/**
 * CEX SDK Exchange Registry
 *
 * Aggregates all exchange modules and provides centralized access to metadata.
 * Each exchange defines its own implementation in its respective folder.
 *
 * @module cex-sdk/exchanges
 */

import type { ExchangeId, ExchangeMetadata } from '../lib/types';

// ============================================================================
// EXCHANGE MODULE EXPORTS
// ============================================================================

// Import and re-export all exchange modules
export * from './binance';
export * from './bingx';
export * from './bitget';
export * from './bitmart';
export * from './bybit';
export * from './coinstore';
export * from './gate';
export * from './hotcoin';
export * from './htx';
export * from './indodax';
export * from './kraken';
export * from './kucoin';
export * from './lbank';
export * from './mexc';
export * from './okx';
export * from './whitebit';
export * from './xt';

// ============================================================================
// METADATA IMPORTS
// ============================================================================

// Import metadata from each exchange module
import { BINANCE_METADATA } from './binance';
import { BINGX_METADATA } from './bingx';
import { BITGET_METADATA } from './bitget';
import { BITMART_METADATA } from './bitmart';
import { BYBIT_METADATA } from './bybit';
import { COINSTORE_METADATA } from './coinstore';
import { GATE_METADATA } from './gate';
import { HOTCOIN_METADATA } from './hotcoin';
import { HTX_METADATA } from './htx';
import { INDODAX_METADATA } from './indodax';
import { KRAKEN_METADATA } from './kraken';
import { KUCOIN_METADATA } from './kucoin';
import { LBANK_METADATA } from './lbank';
import { MEXC_METADATA } from './mexc';
import { OKX_METADATA } from './okx';
import { WHITEBIT_METADATA } from './whitebit';
import { XT_METADATA } from './xt';

// ============================================================================
// METADATA REGISTRY
// ============================================================================

/**
 * Complete registry of all supported exchanges with their metadata.
 * The order follows alphabetical sorting by exchange ID.
 */
export const EXCHANGES_METADATA: readonly ExchangeMetadata[] = [
  BINANCE_METADATA,
  BINGX_METADATA,
  BITGET_METADATA,
  BITMART_METADATA,
  BYBIT_METADATA,
  COINSTORE_METADATA,
  GATE_METADATA,
  HOTCOIN_METADATA,
  HTX_METADATA,
  INDODAX_METADATA,
  KRAKEN_METADATA,
  KUCOIN_METADATA,
  LBANK_METADATA,
  MEXC_METADATA,
  OKX_METADATA,
  WHITEBIT_METADATA,
  XT_METADATA,
] as const;

/**
 * List of exchange IDs that are planned but not yet implemented.
 */
export const PENDING_EXCHANGES: ExchangeId[] = [];

// ============================================================================
// METADATA LOOKUP FUNCTIONS
// ============================================================================

/**
 * Lookup metadata by exchange ID.
 */
export function getExchangeMetadata(id: ExchangeId): ExchangeMetadata | undefined {
  return EXCHANGES_METADATA.find(meta => meta.id === id);
}

/**
 * Get all supported exchange IDs.
 */
export function getSupportedExchangeIds(): ExchangeId[] {
  return EXCHANGES_METADATA.map(meta => meta.id);
}

/**
 * Check if an exchange ID is supported.
 */
export function isExchangeSupported(id: string): id is ExchangeId {
  return EXCHANGES_METADATA.some(meta => meta.id === id);
}
